import Control from "ol/control/Control";

/**
 * Renders a per-layerId revision picker inside the VectorFeatureControl row.
 *
 * The toggle icon is appended into `options.headerEl` (inline with the label).
 * The panel with revision rows is inserted into `options.bodyEl` before the
 * attributes table, so it flows naturally in the document when expanded.
 *
 * Fires "selectedRevisions" property change when any value changes. Its payload
 * is intentionally an array of `{ layerId, revision }` objects only — the extra
 * per-row fields (`latest`, `source`) are internal and not exposed to consumers.
 * Fires "refreshRequested" property change when the refresh button is clicked.
 */
export class RevisionControl extends Control {
  /**
   * @param {{ headerEl: HTMLElement, bodyEl: HTMLElement }} options
   */
  constructor(options = {}) {
    // OL Control needs an element — use a hidden placeholder
    const placeholder = document.createElement("span");
    super({ element: placeholder, target: options.headerEl });

    // ── Toggle button (icon) — lives inline in the header ──────────────────
    const toggleBtn = document.createElement("button");
    toggleBtn.type = "button";
    toggleBtn.className = "revision-toggle-btn";
    toggleBtn.title = "Show revisions";
    const icon = document.createElement("img");
    icon.src = "history.png";
    icon.alt = "Revisions";
    icon.className = "revision-toggle-icon";
    toggleBtn.appendChild(icon);

    // Loading indicator shown inside the button until revisions arrive
    const statusEl = document.createElement("span");
    statusEl.className = "revision-loading";
    statusEl.title = "Loading revisions…";
    statusEl.textContent = "…";
    toggleBtn.appendChild(statusEl);

    // ── Panel — lives in the body, flows inline below the header ───────────
    const panel = document.createElement("div");
    panel.className = "revision-panel";
    panel.hidden = true;

    toggleBtn.addEventListener("click", () => {
      panel.hidden = !panel.hidden;
      toggleBtn.classList.toggle("revision-toggle-btn--open", !panel.hidden);
      toggleBtn.title = panel.hidden ? "Show revisions" : "Hide revisions";
    });

    // Append the icon to the header; insert panel before attributes table
    if (options.headerEl) {
      options.headerEl.appendChild(toggleBtn);
    }
    if (options.bodyEl) {
      // Insert after the header div (first child), before anything else
      const header = options.bodyEl.querySelector(".vector-feature-header");
      if (header && header.nextSibling) {
        options.bodyEl.insertBefore(panel, header.nextSibling);
      } else {
        options.bodyEl.appendChild(panel);
      }
    }

    this._toggleBtn = toggleBtn;
    this._statusEl = statusEl;
    this._panel = panel;
    this._revisions = [];
    this._refreshBtn = null;
    this._productMap = null;
    this._productLabel = "";
    this._preferredSource = "latest";
  }

  /**
   * @param {Array<{layerId,revision,latest?}>} layerRevisions
   * @param {boolean} fromUrl URL restore: keep the passed `revision` values and
   *   only derive labels (non-destructive). In-session: apply preferred source.
   */
  setRevisions(layerRevisions, fromUrl = false) {
    this._revisions = layerRevisions.map((lr) => ({
      layerId: lr.layerId,
      revision: lr.revision,
      // True server-latest: explicit `lr.latest` when the caller overlaid a
      // restored value onto `lr.revision`, else `lr.revision` is the server value.
      latest: lr.latest != null ? lr.latest : lr.revision,
      source: "latest",
    }));
    this._statusEl.remove();
    if (fromUrl) {
      this._deriveSourcesOnly(); // keep restored values, just relabel
    } else {
      this._applyPreferred(); // apply the preferred source if one is already active
    }
    this._render();
  }

  setError(err) {
    this._statusEl.className = "revision-error";
    this._statusEl.textContent = " ⚠";
    this._statusEl.title = "";
    const detail = err?.message ?? "Failed to load revisions";
    this._panel.innerHTML = "";
    const msg = document.createElement("p");
    msg.className = "revision-error";
    msg.textContent = `Revisions unavailable — ${detail}`;
    this._panel.appendChild(msg);
  }

  setLoading() {
    if (this._refreshBtn) {
      this._refreshBtn.disabled = true;
      this._refreshBtn.textContent = "Updating…";
    }
  }

  expand() {
    this._panel.hidden = false;
    this._toggleBtn.classList.add("revision-toggle-btn--open");
    this._toggleBtn.title = "Hide revisions";
  }

  /**
   * Called by ProductControl. map = Map<layerId,revisionId> | null.
   * @param {boolean} fromUrl URL restore: store the map for label derivation
   *   only — never overwrite a row's restored value, never set `_preferredSource`.
   * @returns {number} rows currently sourced "product" after this call.
   */
  setProductMap(map, productLabel, fromUrl = false) {
    if (!map) {
      this.clearProduct();
      return 0;
    }
    this._productMap = map;
    this._productLabel = productLabel || "";
    if (fromUrl) {
      // Re-derive labels against the now-known product map; values untouched.
      this._deriveSourcesOnly();
      this._render();
      this._emitSelected();
      return this._revisions.filter((r) => r.source === "product").length;
    }
    this._preferredSource = "product";
    const changed = this._applyPreferred();
    this._render();
    this._emitSelected();
    return changed;
  }

  /** Rows currently on the product source, as { layerId, revision }. */
  getProductRows() {
    return this._revisions
      .filter((r) => r.source === "product")
      .map((r) => ({ layerId: r.layerId, revision: r.revision }));
  }

  /** Reset all rows back to latest and clear the active product map. */
  clearProduct() {
    this._productMap = null;
    this._productLabel = "";
    this._preferredSource = "latest";
    this._applyPreferred(); // puts every row back on latest
    this._render();
    this._emitSelected();
  }

  /**
   * Derive a single row's label from its current value (no mutation of value):
   * product if it equals the product-map value, else latest if it equals the
   * true server-latest, else custom (modified).
   */
  _deriveSource(r) {
    if (this._productMap && this._productMap.get(r.layerId) === r.revision)
      return "product";
    if (r.revision === r.latest) return "latest";
    return "custom";
  }

  /** Relabel every row from its current value, keeping all values intact. */
  _deriveSourcesOnly() {
    for (const r of this._revisions) r.source = this._deriveSource(r);
  }

  /**
   * Apply the currently preferred source to every row.
   * - preferred "product" (with a map): governed rows -> product value (source
   *   "product"), the rest -> latest.
   * - preferred "latest" (or no map): every row -> latest.
   * Deliberately overrides custom/manual rows — the explicit Latest/product
   * actions, bulk buttons and re-fetch are allowed to clobber manual edits; the
   * drawer Undo is the safety net.
   * @returns {number} rows set to source "product" on this call.
   */
  _applyPreferred() {
    let changed = 0;
    for (const r of this._revisions) {
      if (this._preferredSource === "product" && this._productMap) {
        if (this._productMap.has(r.layerId)) {
          r.revision = this._productMap.get(r.layerId);
          r.source = "product";
          changed++;
        } else {
          r.revision = r.latest;
          r.source = "latest";
        }
      } else {
        r.revision = r.latest;
        r.source = "latest";
      }
    }
    return changed;
  }

  _emitSelected() {
    this.set(
      "selectedRevisions",
      this._revisions.map((r) => ({
        layerId: r.layerId,
        revision: r.revision,
      })),
    );
  }

  /**
   * Public emit hook for the one caller that applies a selection
   * ASYNCHRONOUSLY and then needs it published: the "↺ Latest" refetch in
   * `WFSLayersAndControls`. Emits the actual current row values so the URL
   * serializer reflects exactly what is displayed. In-control actions
   * (product / manual / bulk) emit via `_emitSelected` directly.
   */
  emitSelected() {
    this._emitSelected();
  }

  // ── private ────────────────────────────────────────────────────────────────

  _render() {
    this._panel.innerHTML = "";
    if (this._revisions.length === 0) return;

    this._revisions.forEach((lr) => {
      const row = document.createElement("div");
      row.className = "revision-row";

      const label = document.createElement("span");
      label.className = "revision-layer-id";
      label.textContent = `layer ${lr.layerId}`;

      const minusBtn = document.createElement("button");
      minusBtn.className = "revision-btn";
      minusBtn.type = "button";
      minusBtn.textContent = "−";

      const input = document.createElement("input");
      input.type = "number";
      input.className = "revision-input";
      input.value = String(lr.revision);
      input.min = "0";

      const plusBtn = document.createElement("button");
      plusBtn.className = "revision-btn";
      plusBtn.type = "button";
      plusBtn.textContent = "+";

      minusBtn.addEventListener("click", () => {
        const c = parseInt(input.value, 10) || 0;
        if (c > 0) input.value = String(c - 1);
        this._onManualEdit(lr, input);
      });
      plusBtn.addEventListener("click", () => {
        const c = parseInt(input.value, 10) || 0;
        input.value = String(c + 1);
        this._onManualEdit(lr, input);
      });
      input.addEventListener("change", () => this._onManualEdit(lr, input));

      row.appendChild(label);
      row.appendChild(minusBtn);
      row.appendChild(input);
      row.appendChild(plusBtn);
      row.appendChild(this._buildSourceBadge(lr));
      this._panel.appendChild(row);
    });

    const actions = document.createElement("div");
    actions.className = "revision-actions";

    const latestBtn = document.createElement("button");
    latestBtn.className = "revision-refresh-btn";
    latestBtn.type = "button";
    latestBtn.textContent = "↺ Latest";
    latestBtn.title = "Re-fetch the latest revisions from the server";
    latestBtn.addEventListener("click", () => {
      this._preferredSource = "latest";
      this.set("refreshRequested", Date.now());
    });
    this._refreshBtn = latestBtn;
    actions.appendChild(latestBtn);

    const productBtn = document.createElement("button");
    productBtn.className = "revision-refresh-btn";
    productBtn.type = "button";
    productBtn.textContent = "↺ product";
    if (!this._productMap) {
      productBtn.disabled = true;
      productBtn.title = "Select a product first";
    } else if (!this._revisions.some((r) => this._productMap.has(r.layerId))) {
      productBtn.disabled = true;
      productBtn.title = "No product revisions for this layer";
    } else {
      productBtn.title = "Apply the selected product's revisions";
    }
    productBtn.addEventListener("click", () => {
      this._preferredSource = "product";
      this._applyPreferred();
      this._render();
      this._emitSelected();
    });
    actions.appendChild(productBtn);

    this._panel.appendChild(actions);
  }

  /** Read-only right-flush badge showing the row's source: P / M / L. */
  _buildSourceBadge(lr) {
    const badge = document.createElement("span");
    const kind =
      lr.source === "product" ? "p" : lr.source === "custom" ? "m" : "l";
    badge.className = `revision-badge revision-badge--${kind}`;
    badge.textContent =
      lr.source === "product" ? "P" : lr.source === "custom" ? "M" : "L";
    const title =
      lr.source === "product"
        ? "On the product revision"
        : lr.source === "custom"
          ? "Manually set"
          : "On the latest revision";
    badge.title = title;
    badge.setAttribute("aria-label", title);
    return badge;
  }

  _onManualEdit(lr, input) {
    lr.revision = parseInt(input.value, 10) || 0;
    lr.source = "custom";
    this._render();
    this._emitSelected();
  }

  /**
   * Explicit drawer bulk action ("All to product" / "All to latest"). Sets the
   * preferred source and re-applies it across all rows, deliberately
   * OVERRIDING manual (custom) rows.
   */
  bulkSetSource(source) {
    this._preferredSource = source === "product" ? "product" : "latest";
    this._applyPreferred();
    this._render();
    this._emitSelected();
  }
}
